// SignalsCatalogPanel — the right-column "all signals" reference shown when the
// signals dimension lens is focused (focusDim === "signals"). The center panel
// is the alert console (what FIRED in this window); this is the complementary
// catalog of every DETECTION signal in the tenant's catalog — title, command,
// enabled state, description — so the analyst can read the whole detection
// surface at a glance and click into any one. It replaces the Contrast grid
// (endpoints × clusters), which is meaningless when no value-scope is selected.
//
// Registry-driven (ListSignals), same source as the console + sidebar facet, so
// the reference and the live listing never diverge. Shows enabled AND disabled
// detections (the enabled ones badged) — the full surface, not just what's on.
// Read-only: authoring lives in the Library's SignalsPage and the per-signal
// SignalDefinitionPanel. Clicking a row commits the signals scope step (via the
// parent's onScopePathChange, which also clears the dimension focus), landing on
// that signal's detail + definition.
import ScrollArea from "../ScrollArea.jsx";
import { NOTE_CLASS } from "../../lib/ui.js";
import { useSignalCatalog } from "../../lib/signalCatalog.js";

function Chip({ children, className = "" }) {
  return <span className={`border border-[color:var(--line-soft)] px-[5px] py-[1px] text-[10px] text-[color:var(--muted-soft)] ${className}`}>{children}</span>;
}

export default function SignalsCatalogPanel({ onPickSignal }) {
  const { ready, denied, error, detections } = useSignalCatalog();

  // Only detections carry a command / are scopable in the signals dim; extraction
  // signals feed their own lenses and never appear here. De-dup by command (two
  // detections can derive the same slug) so rows keyed on `command` stay unique.
  // Enabled first, then A–Z, so the analyst sees the active surface on top.
  const byCommand = new Map();
  for (const d of detections) if (d.command && !byCommand.has(d.command)) byCommand.set(d.command, d);
  const signals = [...byCommand.values()].sort((a, b) =>
    Number(b.deployedEnabled) - Number(a.deployedEnabled) ||
    String(a.label).localeCompare(String(b.label)),
  );

  if (denied) {
    return (
      <div className="grid h-full place-items-center px-[12px]" aria-busy="false">
        <p className={`${NOTE_CLASS} max-w-[320px] text-center`}>
          You don’t have permission to view signals. Ask an admin for the Signals read role.
        </p>
      </div>
    );
  }
  if (error) {
    return (
      <div className="grid h-full place-items-center px-[12px]" aria-busy="false">
        <p className={`${NOTE_CLASS} max-w-[320px] text-center`}>Couldn’t load signals. Try again shortly.</p>
      </div>
    );
  }
  if (!ready) {
    return (
      <div className="grid h-full place-items-center px-[12px]" aria-busy="true">
        <p className={`${NOTE_CLASS} max-w-[320px] text-center`}>Loading signals…</p>
      </div>
    );
  }
  if (!signals.length) {
    return (
      <div className="grid h-full place-items-center px-[12px]" aria-busy="false">
        <p className={`${NOTE_CLASS} max-w-[320px] text-center`}>
          No detection signals in this tenant’s catalog yet.
        </p>
      </div>
    );
  }

  const enabledCount = signals.filter((s) => s.deployedEnabled).length;
  return (
    <ScrollArea className="min-h-0 h-full" contentClassName="grid content-start gap-[6px] py-[10px] pb-[18px]" aria-busy="false">
      <p className={NOTE_CLASS}>
        {signals.length} detection signal{signals.length === 1 ? "" : "s"} in catalog · {enabledCount} enabled. Select one to view its definition and matching prompts.
      </p>
      {signals.map((s) => (
        <button
          key={s.command}
          type="button"
          onClick={() => onPickSignal?.(s.command)}
          className="group flex flex-col gap-[5px] border border-[color:var(--line)] bg-[color:var(--bg)] px-[10px] py-[8px] text-left transition-colors hover:border-[color:var(--line-strong)] cursor-pointer"
        >
          <span className="text-[12px] leading-[1.25] text-[color:var(--ink)] transition-colors group-hover:text-[color:var(--green)]">
            {s.label || `/${s.command}`}
          </span>
          <span className="flex flex-wrap gap-[5px]">
            <Chip>/{s.command}</Chip>
            <Chip className={s.deployedEnabled ? "text-[color:var(--green)]" : ""}>
              {s.deployedEnabled ? "enabled" : "off"}
            </Chip>
            {s.tier === "audit" ? <Chip>audit</Chip> : null}
          </span>
          {s.description ? (
            <span className="text-[11px] leading-[1.45] text-[color:var(--muted)] [overflow-wrap:anywhere]">
              {s.description}
            </span>
          ) : null}
        </button>
      ))}
    </ScrollArea>
  );
}
